import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HubBrandsPanel, HubClosedBrandsAlert } from "@/components/BrandOpenToggle";
import { fetchOrders, fetchStats, updateOrderStatus, acknowledgeArrival, adminPrintReprint, releasePreorder, bookCourier, cancelCourier } from "@/lib/api";
import {
    STATUS_LABELS,
    NEXT_ACTION_LABEL,
    nextStatus,
    formatMoney,
    minutesAgo,
} from "@/lib/orderUtils";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "routing", label: "Routing" },
    { key: "kitchen", label: "In Kitchen" },
    { key: "ready", label: "Ready" },
    { key: "collected", label: "Collected" },
];

const SCOPES = [
    { key: "active", label: "Active" },
    { key: "scheduled", label: "Scheduled" },
];

const Hub = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState({
        new: 0,
        routing: 0,
        kitchen: 0,
        ready: 0,
        collected: 0,
        total_today: 0,
        revenue_today: 0,
    });
    const [filter, setFilter] = useState("all");
    const [showBrands, setShowBrands] = useState(false);
    const [brandsVersion, setBrandsVersion] = useState(0);
    const [scope, setScope] = useState("active");
    const [tick, setTick] = useState(0);
    const lastNewIdsRef = useRef(new Set());
    const seenIdsRef = useRef(new Set());
    const releasedSeenIdsRef = useRef(new Set());  // ids where the pre-order chime has fired
    const arrivedAckedIdsRef = useRef(new Set());  // ids where we've already played the doorbell
    const hasInitialisedRef = useRef(false);
    const audioCtxRef = useRef(null);
    const topBarRef = useRef(null);
    const [topBarHeight, setTopBarHeight] = useState(220);
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    // One-time iOS audio unlock — Safari requires a user gesture before any
    // AudioContext can produce sound. We attach a single click/touch handler
    // that resumes the context + plays a 1ms silent burst to confirm.
    useEffect(() => {
        if (audioUnlocked) return undefined;
        const unlock = () => {
            try {
                if (!audioCtxRef.current) {
                    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                const ctx = audioCtxRef.current;
                if (ctx.state === "suspended") {
                    ctx.resume().catch(() => {});
                }
                // Tiny silent burst to confirm audio is actually unlocked
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                g.gain.setValueAtTime(0.0001, ctx.currentTime);
                o.start(); o.stop(ctx.currentTime + 0.01);
                setAudioUnlocked(true);
            } catch { /* ignore */ }
        };
        window.addEventListener("click", unlock, { once: true });
        window.addEventListener("touchend", unlock, { once: true });
        return () => {
            window.removeEventListener("click", unlock);
            window.removeEventListener("touchend", unlock);
        };
    }, [audioUnlocked]);
    const [autoPrint, setAutoPrint] = useState(() => {
        try { return localStorage.getItem("naami_hub_autoprint") !== "off"; } catch { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem("naami_hub_autoprint", autoPrint ? "on" : "off"); } catch { /* ignore */ }
    }, [autoPrint]);

    const staff = (() => {
        try {
            return JSON.parse(localStorage.getItem("naami_staff") || "null");
        } catch {
            return null;
        }
    })();

    useEffect(() => {
        if (!staff) navigate("/hub/login");
    }, [staff, navigate]);

    // Re-render every 30s for elapsed times
    useEffect(() => {
        const t = setInterval(() => setTick((x) => x + 1), 30000);
        return () => clearInterval(t);
    }, []);

    // Measure the fixed top bar's actual height so the orders section pads
    // correctly below it across screen sizes / button wrapping.
    useEffect(() => {
        if (!topBarRef.current || typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const h = Math.ceil(e.contentRect.height);
                if (h > 0) setTopBarHeight(h);
            }
        });
        ro.observe(topBarRef.current);
        return () => ro.disconnect();
    }, []);

    // ===== Persistent alert: ping every 5s while any "new" order is un-accepted =====
    // Staff can silence the current batch with the Silence button — sound resumes
    // when the next new order arrives (silenceUntilCount changes).
    const [silenceCount, setSilenceCount] = useState(0); // count threshold at the moment Silence was tapped
    const pendingNewCount = scope === "active" ? orders.filter((o) => o.status === "new").length : 0;
    const shouldAlert = pendingNewCount > 0 && pendingNewCount > silenceCount && hasInitialisedRef.current;

    const playBeep = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g);
            g.connect(ctx.destination);
            o.frequency.value = 880;
            o.type = "sine";
            g.gain.setValueAtTime(0.0, ctx.currentTime);
            g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02);
            g.gain.linearRampToValueAtTime(0.0, ctx.currentTime + 0.3);
            o.start();
            o.stop(ctx.currentTime + 0.32);
        } catch {
            /* ignore */
        }
    }, []);

    // Doorbell — two-tone descending chime, distinct from the new-order beep.
    const playDoorbell = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            const playTone = (freq, startOffset, dur) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g);
                g.connect(ctx.destination);
                o.type = "triangle";
                o.frequency.value = freq;
                const t0 = ctx.currentTime + startOffset;
                g.gain.setValueAtTime(0.0, t0);
                g.gain.linearRampToValueAtTime(0.22, t0 + 0.04);
                g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
                o.start(t0);
                o.stop(t0 + dur + 0.02);
            };
            playTone(660, 0, 0.45);   // E5
            playTone(494, 0.35, 0.6); // B4 — "ding-dong"
        } catch {
            /* ignore */
        }
    }, []);

    // Pre-order arrived — distinct rising 3-note fanfare so staff know a
    // scheduled order has just entered the active queue (vs a walk-in beep).
    const playPreorderChime = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === "suspended") ctx.resume().catch(() => {});
            const playTone = (freq, startOffset, dur) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g);
                g.connect(ctx.destination);
                o.type = "sine";
                o.frequency.value = freq;
                const t0 = ctx.currentTime + startOffset;
                g.gain.setValueAtTime(0.0, t0);
                g.gain.linearRampToValueAtTime(0.20, t0 + 0.03);
                g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
                o.start(t0);
                o.stop(t0 + dur + 0.02);
            };
            // Rising fanfare: C5 → E5 → G5
            playTone(523, 0.00, 0.28);
            playTone(659, 0.22, 0.30);
            playTone(784, 0.44, 0.55);
        } catch {
            /* ignore */
        }
    }, []);

    // Urgent triple-chirp used by the persistent alert (more attention-grabbing
    // than the one-shot playBeep used for the initial new-order ping).
    const playUrgent = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === "suspended") ctx.resume().catch(() => {});
            const chirp = (freq, startOffset) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                o.type = "square";
                o.frequency.value = freq;
                const t0 = ctx.currentTime + startOffset;
                g.gain.setValueAtTime(0.0, t0);
                g.gain.linearRampToValueAtTime(0.30, t0 + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
                o.start(t0); o.stop(t0 + 0.20);
            };
            chirp(880, 0);
            chirp(1320, 0.22);
            chirp(880, 0.44);
        } catch { /* ignore */ }
    }, []);

    const refresh = useCallback(async () => {
        try {
            const [list, s] = await Promise.all([fetchOrders(filter, scope), fetchStats()]);
            setOrders(list);
            setStats(s);
            // Only run new-order / arrival detection on the ACTIVE feed —
            // the Scheduled tab intentionally doesn't beep, print, or fire
            // any alerts (those are held pre-orders, not kitchen work).
            if (scope !== "active") {
                for (const o of list) seenIdsRef.current.add(o.id);
                hasInitialisedRef.current = true;
                return;
            }
            // Detect newly-arrived "new" orders -> play alert + (optional) auto-print
            const currentNew = new Set(list.filter((o) => o.status === "new").map((o) => o.id));
            const trulyNewIds = [];
            const trulyNewPreorderIds = [];  // released pre-orders — distinct chime
            for (const id of currentNew) {
                if (!seenIdsRef.current.has(id)) {
                    const order = list.find((o) => o.id === id);
                    const wasPreorder = order && order.released_at && !releasedSeenIdsRef.current.has(id);
                    if (wasPreorder) trulyNewPreorderIds.push(id);
                    else trulyNewIds.push(id);
                }
            }
            if (trulyNewIds.length > 0) {
                playBeep();
                // Skip auto-print on the very first refresh after Hub mount (otherwise
                // every existing "new" order would open a print tab).
                if (hasInitialisedRef.current && autoPrint) {
                    for (const id of trulyNewIds) {
                        window.open(`/print/${id}`, "_blank", "noopener");
                    }
                }
            }
            if (trulyNewPreorderIds.length > 0 && hasInitialisedRef.current) {
                playPreorderChime();
                if (autoPrint) {
                    for (const id of trulyNewPreorderIds) {
                        window.open(`/print/${id}`, "_blank", "noopener");
                    }
                }
                for (const id of trulyNewPreorderIds) releasedSeenIdsRef.current.add(id);
            }

            // Customer-arrived detection (independent of new-order detection):
            // play a doorbell once when any unacknowledged arrival shows up.
            const arrivedPending = list.filter((o) =>
                o.customer_arrived_at && !o.arrival_acknowledged_at &&
                !["collected", "cancelled"].includes(o.status)
            );
            const newlyArrivedIds = arrivedPending
                .map((o) => o.id)
                .filter((id) => !arrivedAckedIdsRef.current.has(id));
            if (newlyArrivedIds.length > 0 && hasInitialisedRef.current) {
                playDoorbell();
            }
            // Track ids we've seen as pending; clear once they're acked / collected
            arrivedAckedIdsRef.current = new Set(arrivedPending.map((o) => o.id));
            // Track every order id we've ever seen (across all statuses) so re-listing
            // a still-"new" order doesn't re-trigger the alert.
            for (const o of list) seenIdsRef.current.add(o.id);
            hasInitialisedRef.current = true;
            lastNewIdsRef.current = currentNew;
        } catch (e) {
            console.error(e);
        }
    }, [filter, scope, playBeep, playDoorbell, playPreorderChime, autoPrint]);

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 4000);
        return () => clearInterval(id);
    }, [refresh]);

    // Persistent alert — ping every 5s until ALL new orders are accepted.
    // Stops automatically when pendingNewCount drops to 0.
    useEffect(() => {
        if (!shouldAlert) return undefined;
        // Fire one immediately, then on interval
        playUrgent();
        const t = setInterval(playUrgent, 5000);
        return () => clearInterval(t);
    }, [shouldAlert, playUrgent]);

    // Auto-reset silence threshold once the queue clears OR a new order
    // arrives that wasn't counted at silence time.
    useEffect(() => {
        if (pendingNewCount === 0 && silenceCount !== 0) setSilenceCount(0);
        // If MORE new orders arrive after silencing, drop the silence so the
        // new ones still alert.
        if (silenceCount > 0 && pendingNewCount > silenceCount) setSilenceCount(0);
    }, [pendingNewCount, silenceCount]);

    // Browser tab title reflects pending count so it's visible from afar / in
    // background tabs.
    useEffect(() => {
        const base = "Naami · Hub";
        document.title = pendingNewCount > 0 ? `(${pendingNewCount}) ${base}` : base;
        return () => { document.title = base; };
    }, [pendingNewCount]);

    const advance = async (order) => {
        const next = nextStatus(order.status);
        if (!next) return;
        await updateOrderStatus(order.id, next);
        await refresh();
    };

    const logout = () => {
        localStorage.removeItem("naami_staff");
        navigate("/hub/login");
    };

    const openPrint = (id) => {
        window.open(`/print/${id}`, "_blank", "noopener");
    };

    const reprintToEpson = async (order) => {
        try {
            const r = await adminPrintReprint(order.id);
            const msg = r.queued
                ? `Re-queued ${r.queued} slip(s) for #${order.order_number}.\nThe Epson should print on its next poll.`
                : `No slips queued. Check printer configuration in Admin → Business settings.`;
            alert(msg);
        } catch (e) {
            alert(e.response?.data?.detail || "Reprint failed");
        }
    };

    const onAckArrival = async (order) => {
        try {
            await acknowledgeArrival(order.id);
            // clear flash flag locally; backend already persisted
            arrivedAckedIdsRef.current.delete(order.id);
            await refresh();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Could not acknowledge — try again.");
        }
    };

    const onReleasePreorder = async (order) => {
        if (!window.confirm(`Release pre-order #${order.order_number} into the active queue now?\n\nSlips will print immediately.`)) return;
        try {
            await releasePreorder(order.id);
            // Reset seen-tracking so the Active feed will pick this up as a
            // newly-released order and play the pre-order chime.
            seenIdsRef.current.delete(order.id);
            releasedSeenIdsRef.current.delete(order.id);
            await refresh();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Could not release — try again.");
        }
    };

    const onBookCourier = async (order) => {
        if (!window.confirm(`Book Gophr courier for order #${order.order_number}?\n\nDriver dispatched immediately.`)) return;
        try {
            const r = await bookCourier(order.id);
            if (r.already_booked) alert("Already booked — showing existing courier.");
            await refresh();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Could not book courier — check Gophr credentials and try again.");
        }
    };

    const onCancelCourier = async (order) => {
        if (!window.confirm(`Cancel the Gophr courier for #${order.order_number}?\n\nThis only works before the driver has picked up.`)) return;
        try {
            await cancelCourier(order.id);
            await refresh();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Could not cancel — driver may have already collected.");
        }
    };

    return (
        <div className={`min-h-screen bg-[var(--bg)] text-[var(--text)] ${shouldAlert ? "hub-pending-pulse" : ""}`} data-testid="hub-page">
            {showBrands && <HubBrandsPanel onClose={() => setShowBrands(false)} onChanged={() => setBrandsVersion((v) => v + 1)} />}
            {!audioUnlocked && (
                <div
                    className="fixed inset-x-0 top-0 z-[60] bg-gold text-[#0a0a0a] font-mono text-[11px] uppercase tracking-[0.22em] py-2.5 px-4 text-center cursor-pointer hover:bg-yellow-400 transition shadow-lg"
                    data-testid="hub-audio-unlock-banner"
                >
                    🔊 Tap anywhere to enable order alerts
                </div>
            )}
            {shouldAlert && (
                <div
                    className="fixed inset-x-0 top-0 h-1 bg-[#ff5246] animate-pulse z-50 pointer-events-none"
                    data-testid="hub-alert-strip"
                />
            )}
            {/* Top bar — fixed so it always stays visible, even on iPad Safari. */}
            <div
                ref={topBarRef}
                className="border-b border-[var(--border)] fixed inset-x-0 bg-[var(--bg)] z-30"
                style={{ top: (audioUnlocked ? 0 : 38) + "px" }}
                data-testid="hub-top-bar"
            >
                <HubClosedBrandsAlert onManage={() => setShowBrands(true)} refreshKey={brandsVersion} />
                <div className="container-nk py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="font-display text-xl tracking-tight">naami<span className="text-gold">.</span></div>
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Hub · Live</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hidden md:block">
                            {staff?.name || "Operator"}
                        </span>
                        {shouldAlert && (
                            <button
                                type="button"
                                onClick={() => setSilenceCount(pendingNewCount)}
                                className="px-3 py-1.5 border-2 border-[#ff5246] text-[#ff5246] font-mono text-[10px] uppercase tracking-[0.22em] animate-pulse hover:bg-[#ff5246] hover:text-white transition"
                                data-testid="hub-silence-alert"
                                title="Mute the alarm for the orders already in the queue. New incoming orders will still ring."
                            >
                                🔕 Silence ({pendingNewCount})
                            </button>
                        )}
                        <label className="flex items-center gap-2 cursor-pointer select-none" title="Auto-open the print window for every new incoming order">
                            <input
                                type="checkbox"
                                checked={autoPrint}
                                onChange={(e) => setAutoPrint(e.target.checked)}
                                className="accent-[var(--gold)] w-4 h-4"
                                data-testid="hub-autoprint-toggle"
                            />
                            <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${autoPrint ? "text-gold" : "text-muted"}`}>
                                Auto-print
                            </span>
                        </label>
                        <button onClick={() => setShowBrands(true)} className="btn-ghost" data-testid="hub-open-brands">
                            🏪 Brands
                        </button>
                        <Link
                            to="/hub/print-station"
                            target="_blank"
                            rel="noopener"
                            className="btn-ghost"
                            data-testid="hub-open-print-station"
                            title="Open the auto-print station (Windows PC + Epson)"
                        >
                            🖨 Station ↗
                        </Link>
                        <button onClick={logout} className="btn-ghost" data-testid="hub-logout">
                            Sign out
                        </button>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="border-t border-[var(--border)]">
                    <div className={`container-nk grid grid-cols-2 ${staff?.role === "manager" ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
                        <Stat label="New" value={stats.new} color="var(--status-new)" />
                        <Stat label="Routing" value={stats.routing} color="var(--status-routing)" />
                        <Stat label="Kitchen" value={stats.kitchen} color="var(--status-kitchen)" />
                        <Stat label="Ready" value={stats.ready} color="var(--status-ready)" />
                        <Stat label="Today" value={stats.total_today} color="var(--text)" />
                        {staff?.role === "manager" && stats.revenue_today != null && (
                            <Stat label="Revenue" value={formatMoney(stats.revenue_today)} color="var(--gold)" />
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="border-t border-[var(--border)] overflow-x-auto">
                    <div className="container-nk py-3 flex gap-2 min-w-max items-center">
                        {SCOPES.map((sc) => {
                            const active = scope === sc.key;
                            const badgeCount = sc.key === "scheduled" ? (stats.scheduled_pending || 0) : null;
                            return (
                                <button
                                    key={sc.key}
                                    onClick={() => setScope(sc.key)}
                                    className={`px-4 py-2 font-mono uppercase text-[10px] tracking-[0.22em] border transition-colors flex items-center gap-2 ${
                                        active
                                            ? "bg-[var(--gold)] text-[var(--bg)] border-[var(--gold)]"
                                            : "border-[var(--border)] hover:border-[var(--gold)] text-[var(--text)]"
                                    }`}
                                    data-testid={`hub-scope-${sc.key}`}
                                >
                                    {sc.label}
                                    {badgeCount !== null && badgeCount > 0 && (
                                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${active ? "bg-[var(--bg)] text-[var(--gold)]" : "bg-[var(--gold)] text-[var(--bg)]"}`}>
                                            {badgeCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        <div className="w-px h-6 bg-[var(--border)] mx-2" />
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className={`px-4 py-2 font-mono uppercase text-[10px] tracking-[0.22em] border transition-colors ${
                                    filter === f.key
                                        ? "bg-[var(--gold)] text-[var(--bg)] border-[var(--gold)]"
                                        : "border-[var(--border)] hover:border-[var(--gold)] text-[var(--text)]"
                                }`}
                                data-testid={`hub-filter-${f.key}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Orders — top padding compensates for the fixed header (measured via ResizeObserver) */}
            <div
                className="max-w-7xl mx-auto px-6 py-8"
                style={{ paddingTop: (topBarHeight + (audioUnlocked ? 32 : 70)) + "px" }}
                key={tick}
            >
                {orders.length === 0 ? (
                    <div className="border border-dashed border-[var(--border)] p-12 text-center">
                        <p className="overline mb-2">// {scope === "scheduled" ? "No pre-orders held" : "Quiet on the pass"}</p>
                        <p className="text-muted">
                            {scope === "scheduled"
                                ? "Any pre-order booked more than 30 minutes ahead will land here until 45 mins before pickup — then it auto-releases to the kitchen."
                                : "No orders matching this filter. They'll appear here in real time."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {orders.map((o) => (
                            <OrderCard
                                key={o.id}
                                order={o}
                                onAdvance={() => advance(o)}
                                onPrint={() => openPrint(o.id)}
                                onReprintEpson={() => reprintToEpson(o)}
                                onAcknowledgeArrival={() => onAckArrival(o)}
                                onRelease={() => onReleasePreorder(o)}
                                onBookCourier={() => onBookCourier(o)}
                                onCancelCourier={() => onCancelCourier(o)}
                                tick={tick}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Stat = ({ label, value, color }) => (
    <div className="p-4 border-r border-[var(--border)] last:border-r-0">
        <div className="overline">{label}</div>
        <div className="font-display text-3xl mt-1" style={{ color }}>
            {value}
        </div>
    </div>
);

const OrderCard = ({ order, onAdvance, onPrint, onReprintEpson, onAcknowledgeArrival, onRelease, onBookCourier, onCancelCourier, tick }) => {
    const [showGophrHistory, setShowGophrHistory] = useState(false);
    const isNew = order.status === "new";
    const mins = minutesAgo(order.created_at);
    const urgent = mins >= 10 && order.status !== "collected";
    const next = nextStatus(order.status);
    const customerArrived = !!order.customer_arrived_at;
    const arrivalAcked = !!order.arrival_acknowledged_at;
    const arrivalPending = customerArrived && !arrivalAcked && !["collected", "cancelled"].includes(order.status);
    const isScheduled = !!order.is_scheduled;
    const isDelivery = order.order_type === "delivery";
    const gophr = order.gophr || null;
    const gophrBooked = !!(gophr && gophr.job_id);
    const gophrHistory = order.gophr_history || [];
    const gophrCancellable = gophrBooked && !["EN_ROUTE", "AT_PICKUP", "AT_DELIVERY", "DELIVERED", "TAKING_SIGNATURE", "CANCELED", "CANCELED_BY_CUSTOMER", "CANCELED_BY_COURIER"].includes((gophr?.status || "").toUpperCase());

    // For scheduled pre-orders, compute a "T-minus" countdown to auto-release
    // (which fires 45 mins before scheduled_for). `tick` bumps every 30s so
    // this re-renders naturally.
    let scheduledCountdown = null;
    if (isScheduled && order.scheduled_for) {
        const scheduledDate = new Date(order.scheduled_for);
        const releaseAt = new Date(scheduledDate.getTime() - 45 * 60 * 1000);
        const now = new Date();
        const msUntilRelease = releaseAt - now;
        if (msUntilRelease > 0) {
            const totalMinutes = Math.floor(msUntilRelease / 60000);
            const hrs = Math.floor(totalMinutes / 60);
            const mn = totalMinutes % 60;
            scheduledCountdown = hrs > 0 ? `${hrs}h ${mn}m` : `${mn}m`;
        } else {
            scheduledCountdown = "any moment now";
        }
        // Silence tick-lint warning when not scheduled (still referenced elsewhere via key={tick})
        void tick;
    }

    const groupedLines = order.lines.reduce((acc, l) => {
        (acc[l.brand_token] = acc[l.brand_token] || { name: l.brand_name, items: [] }).items.push(l);
        return acc;
    }, {});

    return (
        <div
            className={`bg-[var(--surface)] border p-5 ${isNew ? "pulse-new" : "border-[var(--border)]"}`}
            style={{ borderLeft: `3px solid var(--status-${order.status})` }}
            data-testid={`hub-order-${order.order_number}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div>
                    <span className="overline">Order</span>
                    <div className="font-display text-2xl">#{order.order_number}</div>
                </div>
                <div className="text-right">
                    <span
                        className={`overline px-2 py-1 border status-${order.status}`}
                    >
                        {STATUS_LABELS[order.status]}
                    </span>
                    <div
                        className={`font-mono text-xs mt-1 ${
                            urgent ? "text-[#ff5246]" : "text-muted"
                        }`}
                    >
                        {mins}m ago
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
                <span className="overline border border-[var(--border)] px-2 py-0.5">
                    {order.source}
                </span>
                {Object.keys(groupedLines).length > 1 && (
                    <span className="overline border border-[var(--gold)] text-gold px-2 py-0.5">
                        Multi-brand · {Object.keys(groupedLines).length}
                    </span>
                )}
                {order.scheduled_for && (
                    <span className={`overline border px-2 py-0.5 ${
                        isScheduled
                            ? "border-[#a855f7] text-[#a855f7] bg-[#a855f7]/10 font-bold"
                            : "border-[var(--status-new)] text-[var(--status-new)]"
                    }`} data-testid="hub-scheduled">
                        {isScheduled ? "⏳ " : "⏰ "}
                        {new Date(order.scheduled_for).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                )}
                {isScheduled && (
                    <span className="overline bg-[#a855f7] text-white px-2 py-0.5 font-bold" data-testid={`hub-preorder-${order.order_number}`}>
                        PRE-ORDER
                    </span>
                )}
                {!isScheduled && order.released_at && (
                    <span className="overline border border-[#a855f7] text-[#a855f7] px-2 py-0.5" title={`Released at ${new Date(order.released_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}>
                        ↑ Released
                    </span>
                )}
                {arrivalPending && (
                    <span
                        className="overline bg-[#e1262b] text-white px-2 py-0.5 font-bold animate-pulse"
                        data-testid={`hub-arrived-${order.order_number}`}
                        title={`Customer arrived ${new Date(order.customer_arrived_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                    >
                        📍 ARRIVED
                    </span>
                )}
                {customerArrived && arrivalAcked && order.status !== "collected" && (
                    <span className="overline border border-[var(--gold)] text-gold px-2 py-0.5">
                        ✓ Notified
                    </span>
                )}
                {order.loyalty_member_id && (
                    <span className="overline border border-[var(--gold)] text-gold px-2 py-0.5">
                        ✦ Club
                    </span>
                )}
                {order.pay_method === "cash" ? (
                    <span className="overline border border-[#ff5246] text-[#ff5246] px-2 py-0.5">
                        Cash · counter
                    </span>
                ) : (
                    <span className="overline border border-[#34c759] text-[#34c759] px-2 py-0.5">
                        Card · paid
                    </span>
                )}
            </div>

            {order.customer_name && (
                <div className="text-sm mb-2">
                    <span className="overline mr-2">Name</span>
                    {order.customer_name}
                </div>
            )}

            {order.order_type === "delivery" && order.delivery && (
                <div className="border-2 border-[#ff9f0a] bg-[#ff9f0a]/10 p-3 mb-3" data-testid={`hub-delivery-${order.id}`}>
                    <div className="font-display text-[14px] tracking-wider text-[#ff9f0a] mb-1">★ DELIVERY ORDER</div>
                    <div className="text-sm leading-snug font-medium">
                        {order.delivery.address_line_1}
                        {order.delivery.address_line_2 && `, ${order.delivery.address_line_2}`}
                        {order.delivery.city && `, ${order.delivery.city}`}
                        <span className="font-mono"> · {order.delivery.postcode}</span>
                    </div>
                    <div className="font-mono text-sm text-[#ff9f0a] mt-1">
                        ☎ <a href={`tel:${order.delivery.phone}`} className="underline">{order.delivery.phone}</a>
                    </div>
                    {order.delivery.instructions && (
                        <div
                            className="mt-2 border-2 border-[#ff5246] bg-[#ff5246]/15 p-2"
                            data-testid={`hub-driver-instructions-${order.order_number}`}
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5246] font-bold mb-0.5">
                                !! Driver instructions
                            </div>
                            <div className="text-sm font-bold leading-snug">
                                {order.delivery.instructions}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="border-t border-[var(--border)] pt-3 space-y-3 mb-3">
                {Object.entries(groupedLines).map(([token, g]) => {
                    // Group this brand's items by category, preserving insertion order.
                    const byCat = {};
                    const catOrder = [];
                    for (const l of g.items) {
                        const c = (l.category || "").trim() || "Other";
                        if (!byCat[c]) { byCat[c] = []; catOrder.push(c); }
                        byCat[c].push(l);
                    }
                    const brandNote = order.brand_notes?.[g.items[0]?.brand_id] || null;
                    return (
                        <div key={token}>
                            <div className="font-mono text-xs text-muted mb-1">
                                [{token}] {g.name}
                            </div>
                            {catOrder.map((cat) => (
                                <div key={cat} className="mb-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold pl-1">// {cat}</div>
                                    {byCat[cat].map((l) => (
                                        <div
                                            key={`${l.item_id}-${(l.modifiers || []).map((m) => m.option_name).join("|")}`}
                                            className="flex justify-between text-sm pl-3"
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="truncate">{l.qty}× {l.name}</span>
                                                {l.modifiers?.length > 0 && (
                                                    <span className="block font-mono text-[11px] text-muted pl-2">
                                                        {l.modifiers.map((m) => `+ ${m.option_name}`).join(" · ")}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="font-mono text-muted whitespace-nowrap ml-2">
                                                {formatMoney(l.price * l.qty)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                            {brandNote && (
                                <div className="mt-1 border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-1.5 text-xs" data-testid={`hub-brand-note-${token}`}>
                                    <span className="overline text-[#ff9f0a] block">Kitchen note</span>
                                    {brandNote}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {order.notes && (
                <div className="border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-2 text-xs mb-3">
                    <span className="overline text-[#ff9f0a] block mb-1">Notes</span>
                    {order.notes}
                </div>
            )}

            <div className="flex items-center justify-between mb-4">
                <span className="overline">Total</span>
                <span className="font-mono text-lg">{formatMoney(order.total)}</span>
            </div>

            {isScheduled && (
                <div className="border-2 border-[#a855f7] bg-[#a855f7]/10 p-3 mb-3" data-testid={`hub-preorder-panel-${order.order_number}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#a855f7] mb-1">// Held for later</div>
                    <div className="text-sm mb-1">
                        Auto-releases to kitchen in <span className="font-mono font-bold">{scheduledCountdown || "—"}</span>
                    </div>
                    <div className="font-mono text-[11px] text-muted">Pickup: {new Date(order.scheduled_for).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
            )}

            {isDelivery && !isScheduled && (
                <div className={`border-2 p-3 mb-3 ${gophrBooked ? "border-[#3ea7ff] bg-[#3ea7ff]/10" : "border-[var(--gold)] bg-[var(--gold)]/5"}`} data-testid={`hub-gophr-panel-${order.order_number}`}>
                    <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-1 ${gophrBooked ? "text-[#3ea7ff]" : "text-gold"}`}>
                        {gophrBooked ? "// Gophr courier" : "// Delivery — awaiting courier"}
                    </div>
                    {gophrBooked ? (
                        <div className="text-sm space-y-0.5">
                            <div>Status: <span className="font-mono font-bold">{(gophr.status || "CONFIRMED").replace(/_/g, " ")}</span></div>
                            {gophr.courier_name && <div>Driver: <span className="font-mono">{gophr.courier_name}</span></div>}
                            {gophr.courier_phone && <div>Phone: <a className="font-mono underline" href={`tel:${gophr.courier_phone}`}>{gophr.courier_phone}</a></div>}
                            {gophr.delivery_eta && <div className="text-muted text-xs">ETA: {new Date(gophr.delivery_eta).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
                            {gophr.tracking_url && <a href={gophr.tracking_url} target="_blank" rel="noreferrer" className="inline-block mt-1 font-mono text-[11px] underline text-[#3ea7ff]" data-testid={`hub-tracking-link-${order.order_number}`}>Open live tracker →</a>}
                        </div>
                    ) : (
                        <div className="text-sm text-muted">
                            Tap <strong className="text-gold">Book Courier</strong> when the order is bagged and ready to hand over.
                        </div>
                    )}

                    {/* Gophr history — collapsible timeline of every prior booking
                        attempt on this order. Great for dispute/invoice audits. */}
                    {gophrHistory.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[var(--border)]/60">
                            <button
                                type="button"
                                onClick={() => setShowGophrHistory((v) => !v)}
                                className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold flex items-center gap-1"
                                data-testid={`hub-gophr-history-toggle-${order.order_number}`}
                            >
                                <span>{showGophrHistory ? "▾" : "▸"}</span>
                                History ({gophrHistory.length})
                            </button>
                            {showGophrHistory && (
                                <ol className="mt-2 space-y-2 font-mono text-[11px]" data-testid={`hub-gophr-history-${order.order_number}`}>
                                    {gophrHistory.map((h, i) => {
                                        const booked = h.booked_at ? new Date(h.booked_at) : null;
                                        const cancelled = h.cancelled_at ? new Date(h.cancelled_at) : null;
                                        const status = (h.status || "").replace(/_/g, " ");
                                        return (
                                            <li key={`${h.job_id || i}-${h.booked_at || i}`} className="border-l-2 border-[#ff5246]/60 pl-2">
                                                <div className="text-[#ff9f0a]">Attempt #{i + 1} · {h.job_id || "?"}</div>
                                                {booked && <div>· Booked {booked.toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</div>}
                                                {cancelled && <div>· Cancelled {cancelled.toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}{h.cancelled_via ? ` via ${h.cancelled_via}` : ""}</div>}
                                                {status && <div className="text-muted">Final status: {status}</div>}
                                                {h.tracking_url && <a href={h.tracking_url} target="_blank" rel="noreferrer" className="underline text-[#3ea7ff]">Tracker</a>}
                                            </li>
                                        );
                                    })}
                                </ol>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex gap-2 flex-wrap">
                {isScheduled ? (
                    <>
                        <button
                            onClick={onRelease}
                            className="btn-primary flex-1 text-xs py-3 bg-[#a855f7] hover:opacity-90"
                            style={{ color: "white" }}
                            data-testid={`hub-release-${order.order_number}`}
                        >
                            ⚡ Release now
                        </button>
                        <button
                            onClick={onPrint}
                            className="btn-ghost"
                            data-testid={`hub-print-${order.order_number}`}
                        >
                            View slip
                        </button>
                    </>
                ) : (
                    <>
                        {arrivalPending && (
                            <button
                                onClick={onAcknowledgeArrival}
                                className="btn-primary flex-1 text-xs py-3 bg-[#e1262b] hover:opacity-90"
                                style={{ color: "white" }}
                                data-testid={`hub-ack-arrival-${order.order_number}`}
                            >
                                ✓ Customer notified
                            </button>
                        )}
                        {next && !arrivalPending && (
                            <button
                                onClick={onAdvance}
                                className="btn-primary flex-1 text-xs py-3"
                                data-testid={`hub-advance-${order.order_number}`}
                            >
                                {NEXT_ACTION_LABEL[order.status]}
                            </button>
                        )}
                        {next && arrivalPending && (
                            <button
                                onClick={onAdvance}
                                className="btn-secondary text-xs py-3"
                                data-testid={`hub-advance-${order.order_number}`}
                            >
                                {NEXT_ACTION_LABEL[order.status]}
                            </button>
                        )}
                        {isDelivery && !gophrBooked && (
                            <button
                                onClick={onBookCourier}
                                className="btn-primary text-xs py-3 bg-[#3ea7ff] hover:opacity-90"
                                style={{ color: "white" }}
                                data-testid={`hub-book-courier-${order.order_number}`}
                            >
                                🛵 Book Courier
                            </button>
                        )}
                        {isDelivery && gophrCancellable && (
                            <button
                                onClick={onCancelCourier}
                                className="btn-ghost text-xs py-3"
                                data-testid={`hub-cancel-courier-${order.order_number}`}
                            >
                                Cancel courier
                            </button>
                        )}
                        <button
                            onClick={onPrint}
                            className="btn-ghost"
                            data-testid={`hub-print-${order.order_number}`}
                        >
                            Print
                        </button>
                        <button
                            onClick={onReprintEpson}
                            className="btn-ghost"
                            data-testid={`hub-reprint-epson-${order.order_number}`}
                            title="Re-queue slip to Epson Server Direct Print"
                        >
                            Reprint →
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Hub;
